import {matchEmail, matchNameAndEmail} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {buildContactForHandle, ContactSourcesEnum} from '../../models/api/contactModel';
import {RecipientRolesEnum} from '../../models/api/recipientModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';

/*
 * General types and parse map.
 */

export interface RecipientParserOptions {
  defaultNumberPrefix?: string;
}

export type RecipientParserWithOptions = (
  role: RecipientRolesEnum,
  text: string,
  options?: RecipientParserOptions,
) => VisualRecipientModel | null;
type RecipientParsers = {[T in ContactSourcesEnum]?: RecipientParserWithOptions};

export const recipientParsers: RecipientParsers = {
  [ContactSourcesEnum.EMAIL]: emailRecipientParser,
  [ContactSourcesEnum.PHONE]: phoneRecipientParser,
  [ContactSourcesEnum.CUSTOM]: customRecipientParser,
  [ContactSourcesEnum.TWITTER]: makeTwitterParser(ContactSourcesEnum.TWITTER),
  [ContactSourcesEnum.TWITTER_DM]: makeTwitterParser(ContactSourcesEnum.TWITTER_DM),
};

export function getRecipientParser(source: ContactSourcesEnum) {
  return recipientParsers[source];
}

/*
 * Email.
 */

export function emailRecipientParser(role: RecipientRolesEnum, text: string): VisualRecipientModel | null {
  // Try to match name + email first.
  const nameAndEmailMatch = matchNameAndEmail(text);
  if (nameAndEmailMatch) {
    const {name, email} = nameAndEmailMatch;
    return {
      name: name || undefined,
      namespace: 'global',
      role,
      contact: buildContactForHandle(ContactSourcesEnum.EMAIL, email),
    };
  }

  // If that didn't work, try email by itself.
  const email = matchEmail(text);
  if (email) {
    return buildVisualRecipientForEmail(role, email);
  }

  // Otherwise, nothing was found.
  return null;
}

export function buildVisualRecipientForEmail(role: RecipientRolesEnum, email: string): VisualRecipientModel {
  return {
    namespace: 'global',
    role,
    contact: buildContactForHandle(ContactSourcesEnum.EMAIL, email),
  };
}

/*
 * Phone.
 */

export function phoneRecipientParser(
  role: RecipientRolesEnum,
  text: string,
  options: RecipientParserOptions = {},
): VisualRecipientModel | null {
  const phoneHandle = parsePhoneHandle(text, options.defaultNumberPrefix);
  if (!phoneHandle) {
    return null;
  }

  return {
    namespace: 'global',
    role,
    contact: buildContactForHandle(ContactSourcesEnum.PHONE, phoneHandle),
  };
}

const phoneRegexp = /^\+?[\d ()-]+$/;

function parsePhoneHandle(text: string, defaultNumberPrefix?: string) {
  // Check if this is a valid phone number.
  const phoneMatch = text.match(phoneRegexp);
  if (!phoneMatch) {
    return null;
  }

  // If needed, add a prefix to the number + remove leading "0".
  if (text[0] !== '+' && defaultNumberPrefix) {
    return defaultNumberPrefix + text.replace(/^0/, '');
  }

  return text;
}

/*
 * Custom.
 */

function customRecipientParser(role: RecipientRolesEnum, text: string): VisualRecipientModel | null {
  // We accept any non-empty string.
  if (!text) {
    return null;
  }

  return {
    namespace: 'global',
    role,
    contact: buildContactForHandle(ContactSourcesEnum.CUSTOM, text),
  };
}

/*
 * Twitter.
 */

const twitterRegexp = /^@?(\w{1,15})$/;

function makeTwitterParser(source: ContactSourcesEnum) {
  return function twitterRecipientParser(
    role: RecipientRolesEnum,
    text: string,
  ): VisualRecipientModel | null {
    const twitterMatch = text.match(twitterRegexp);
    if (!twitterMatch) {
      return null;
    }

    const [, username] = twitterMatch;
    const handle = `@${username}`;

    return {
      namespace: 'global',
      role,
      contact: buildContactForHandle(source, handle),
    };
  };
}
